import { cn } from '@/lib/utils';
import { useInjuryRisk } from '@/hooks/useClosedLoopAnalytics';
import { Shield } from 'lucide-react';
import { MetricInfoButton } from '@/components/MetricInfoButton';

interface ClosedLoopRiskPanelProps {
  className?: string;
}

const RISK_CONFIG = {
  low: {
    text: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/20',
    dot: 'bg-emerald-400',
    label: 'LOW',
  },
  moderate: {
    text: 'text-amber-400',
    bg: 'bg-amber-400/10',
    border: 'border-amber-400/20',
    dot: 'bg-amber-400',
    label: 'MODERATE',
  },
  high: {
    text: 'text-orange-400',
    bg: 'bg-orange-400/10',
    border: 'border-orange-400/20',
    dot: 'bg-orange-400',
    label: 'HIGH',
  },
  critical: {
    text: 'text-red-400',
    bg: 'bg-red-400/10',
    border: 'border-red-400/20',
    dot: 'bg-red-400',
    label: 'CRITICAL',
  },
} as const;

function normalizeLevel(level: string): keyof typeof RISK_CONFIG {
  const l = level.toLowerCase();
  if (l === 'low' || l === 'normal') return 'low';
  if (l === 'moderate' || l === 'elevated') return 'moderate';
  if (l === 'high') return 'high';
  if (l === 'critical') return 'critical';
  return 'low';
}

function MetricCell({ label, value, color, metricKey }: { label: string; value: string; color?: string; metricKey?: string }) {
  return (
    <div>
      <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider mb-0.5 flex items-center gap-1">
        {label}
        {metricKey && <MetricInfoButton metricKey={metricKey} />}
      </div>
      <div className={cn('text-sm font-bold tabular-nums font-mono', color ?? 'text-foreground')}>{value}</div>
    </div>
  );
}

export function ClosedLoopRiskPanel({ className }: ClosedLoopRiskPanelProps) {
  const { data, isLoading } = useInjuryRisk();

  if (isLoading) {
    return (
      <div className={cn('p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]', className)}>
        <p className="text-xs font-mono text-muted-foreground text-center animate-pulse">COMPUTING RISK...</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const level = normalizeLevel(data.risk_level ?? 'low');
  const cfg = RISK_CONFIG[level];
  const riskScore = data.risk_score ?? 0;

  return (
    <div className={cn(
      'p-4 rounded-lg border',
      cfg.border, cfg.bg,
      className,
    )}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Shield className={cn('h-4 w-4', cfg.text)} />
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
          INJURY RISK (ENGINE)
        </span>
        <MetricInfoButton metricKey="injuryRisk" />
        <div className="ml-auto flex items-center gap-1.5">
          <div className={cn('w-2 h-2 rounded-full', cfg.dot)} />
          <span className={cn('text-[10px] font-mono font-bold tracking-wider', cfg.text)}>
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Risk score bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Risk Score</span>
          <span className={cn('text-sm font-mono tabular-nums font-bold', cfg.text)}>
            {riskScore.toFixed(0)}/100
          </span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(riskScore, 100)}%`,
              background: `linear-gradient(90deg, hsl(170,80%,45%), ${riskScore > 60 ? 'hsl(15,85%,55%)' : 'hsl(50,80%,55%)'})`,
            }}
          />
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <MetricCell
          label="ACWR"
          value={data.acwr?.toFixed(2) ?? '—'}
          metricKey="acwr"
          color={data.acwr != null
            ? data.acwr > 1.5 ? 'text-red-400' : data.acwr > 1.3 ? 'text-amber-400' : 'text-emerald-400'
            : undefined}
        />
        <MetricCell
          label="Ramp Rate"
          value={data.ramp_rate != null ? `${data.ramp_rate >= 0 ? '+' : ''}${data.ramp_rate.toFixed(1)}` : '—'}
          metricKey="rampRate"
          color={data.ramp_rate != null
            ? data.ramp_rate > 12 ? 'text-red-400' : data.ramp_rate > 8 ? 'text-amber-400' : 'text-emerald-400'
            : undefined}
        />
        <MetricCell
          label="Monotony"
          value={data.monotony?.toFixed(2) ?? '—'}
          metricKey="monotony"
          color={data.monotony != null
            ? data.monotony > 2.0 ? 'text-red-400' : data.monotony > 1.5 ? 'text-amber-400' : 'text-emerald-400'
            : undefined}
        />
        <MetricCell
          label="Fatigue Load"
          value={data.fatigue_load?.toFixed(0) ?? '—'}
          metricKey="fatigueIndex"
        />
      </div>

      {/* Contributing factors */}
      {data.contributing_factors && data.contributing_factors.length > 0 && (
        <div className="p-3 rounded-lg bg-background/50 border border-border/40">
          <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider mb-1.5">
            Contributing Factors
          </div>
          <ul className="space-y-1">
            {data.contributing_factors.slice(0, 5).map((factor, idx) => (
              <li key={idx} className="text-xs text-muted-foreground font-mono">
                <span className={cfg.text}>{'>'}</span> {factor}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
